import { Switch } from "antd";

import { localeLabel } from "@/shared/utils/locale.utils";

import style from "./style.module.css";

interface LanguagesFieldProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  locales: string[];
  disabled?: boolean;
  /** A locale that must stay enabled: its switch is always on and cannot be toggled off. */
  lockedLocale?: string;
  /** Injected by an enclosing Form.Item; also namespaces the per-switch label ids. */
  id?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

// Controlled languages selector (Form.Item child): one switch per offered locale. Each switch is
// named by its visible text via aria-labelledby; the group's name/description come from the caller
// (typically the field label and helper text). Read-only display lives in LanguageChips.
export default function LanguagesField({
  value = [],
  onChange,
  locales,
  disabled = false,
  lockedLocale,
  id,
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
}: LanguagesFieldProps) {
  const toggleLocale = (locale: string, checked: boolean) => {
    onChange?.(checked ? [...value, locale] : value.filter((current) => current !== locale));
  };

  return (
    <ul
      id={id}
      role="group"
      className={style.languageList}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
    >
      {locales.map((locale) => {
        const labelId = `${id ?? "languages"}-toggle-${locale}`;
        const isLocked = locale === lockedLocale;
        return (
          <li key={locale} className={style.languageToggle}>
            <Switch
              checked={isLocked || value.includes(locale)}
              disabled={disabled || isLocked}
              onChange={(checked) => toggleLocale(locale, checked)}
              aria-labelledby={labelId}
            />
            <span id={labelId}>{localeLabel(locale)}</span>
          </li>
        );
      })}
    </ul>
  );
}
